/**
 * Pure, framework-free helpers for foreign-currency expense conversion.
 * Shared by the web and mobile expense forms via `useExpenseConversion`
 * and the `ExpenseFormService` DTO builders.
 */
import {
  getCurrencyDecimals,
  roundToCurrencyPrecision,
} from '@splitfiesta/shared';

const DAY_MS = 86_400_000;
const isoDay = (d: Date) => d.toISOString().slice(0, 10);
const shift = (date: string, days: number) =>
  isoDay(new Date(new Date(`${date}T00:00:00Z`).getTime() + days * DAY_MS));

export const isSameCurrency = (a: string, b: string): boolean =>
  !a || !b || a.toUpperCase() === b.toUpperCase();

export const convertAmount = (
  amount: number,
  rate: number,
  toCurrency: string
): number => roundToCurrencyPrecision(amount * rate, toCurrency);

export const deriveRate = (
  originalAmount: number,
  convertedAmount: number
): number | null =>
  originalAmount > 0 && convertedAmount > 0
    ? convertedAmount / originalAmount
    : null;

/** Closed days never change; yesterday/today/future resolve to the newest close and can move at 00:10Z. */
export const previewStaleTime = (date: string, now = new Date()): number =>
  date < shift(isoDay(now), -1) ? Infinity : 6 * 60 * 60 * 1000;

/** Server rule: pin ≤ expense date and ≥ expense date − 6 days. */
export const isPinUsable = (rateDate: string, expenseDate: string): boolean =>
  rateDate <= expenseDate && rateDate >= shift(expenseDate, -6);

/** Why no reference rate can ever exist for a preview: retrying will not help. */
export type MissingReferenceRate =
  | { reason: 'currency'; currency: string }
  | { reason: 'beforeCoverage'; coverageStart: string };

/**
 * The API maps both cases to a generic `NOT_FOUND` code, so the
 * translation key is what tells them apart from any other 404.
 */
export function missingReferenceRate(
  error: unknown
): MissingReferenceRate | null {
  if (!error || typeof error !== 'object') return null;
  const { translationKey, details } = error as {
    translationKey?: unknown;
    details?: { currency?: unknown; coverageStart?: unknown } | null;
  };
  if (
    translationKey === 'errors:exchangeRate.unavailableForCurrency' &&
    typeof details?.currency === 'string'
  ) {
    return { reason: 'currency', currency: details.currency };
  }
  if (
    translationKey === 'errors:exchangeRate.beforeCoverage' &&
    typeof details?.coverageStart === 'string'
  ) {
    return { reason: 'beforeCoverage', coverageStart: details.coverageStart };
  }
  return null;
}

export interface FrozenRate {
  rate: number;
  rateDate: string;
  source: 'provider' | 'manual';
}

/** A loaded expense's group-currency total and the amount it was entered as. */
export interface StoredAmounts {
  amount: number;
  originalAmount: number;
}

/** The edit form's loaded expense: entered currency, date and stored rate. */
export interface LoadedConversion {
  enteredCurrency: string;
  date: string;
  stored: FrozenRate | null;
  amounts?: StoredAmounts;
}

/**
 * The stored rate applies only while the form still describes the stored
 * pair and day: the server keeps it exactly then, and checks the amount
 * against it. Derived rather than cleared, so moving a value away and back
 * re-freezes instead of leaving today's preview pricing an unchanged patch.
 */
export function deriveFrozenRate(
  loaded: LoadedConversion | null,
  current: { enteredCurrency: string; date: string; manualRate: number | null }
): FrozenRate | null {
  if (!loaded?.stored || current.manualRate !== null) return null;
  return current.enteredCurrency.toUpperCase() ===
    loaded.enteredCurrency.toUpperCase() && current.date === loaded.date
    ? loaded.stored
    : null;
}

/**
 * How an edit form hydrates an expense's stored rate. A server rate always
 * carries its `rateDate` and is frozen; a queued (offline) manual rate has
 * none, and must come back as the manual override or the preview would
 * replace it on save.
 */
export function storedRateForEdit(stored: {
  exchangeRate?: number | null;
  rateDate?: string | null;
  rateSource?: 'provider' | 'manual' | null;
}): { frozen: FrozenRate | null; manualRate: number | null } {
  if (stored.exchangeRate == null) return { frozen: null, manualRate: null };
  if (!stored.rateDate)
    return { frozen: null, manualRate: stored.exchangeRate };
  return {
    frozen: {
      rate: stored.exchangeRate,
      rateDate: stored.rateDate,
      source: stored.rateSource ?? 'provider',
    },
    manualRate: null,
  };
}

/**
 * A queued expense edited with no rate available goes back to the queue in
 * the entered currency, for the sync runner to convert. Any conversion the
 * row carried belongs to the old values.
 */
export function pendingFieldsForQueuedEdit(
  targetCurrency: string,
  existing: { originalCurrency?: string }
): {
  pendingConversion: true;
  targetCurrency: string;
  originalCurrency?: null;
} {
  return {
    pendingConversion: true,
    targetCurrency,
    ...(existing.originalCurrency ? { originalCurrency: null } : {}),
  };
}

export interface ConversionSnapshot {
  isForeign: boolean;
  enteredAmount: number;
  enteredCurrency: string;
  effectiveRate: number | null;
  rateDate: string | null;
  rateSource: 'provider' | 'manual' | null;
  /** YYYY-MM-DD of the expense, for the pin window */
  expenseDate: string;
}

export interface CreateConversionFields {
  originalAmount: number;
  originalCurrency: string;
  exchangeRate?: number;
  rateDate?: string;
}

export function conversionFieldsForCreate(
  s: ConversionSnapshot
): CreateConversionFields | null {
  if (!s.isForeign || s.effectiveRate === null) return null;
  const base = {
    originalAmount: s.enteredAmount,
    originalCurrency: s.enteredCurrency.toUpperCase(),
  };
  if (s.rateSource === 'manual')
    return { ...base, exchangeRate: s.effectiveRate };
  return s.rateDate && isPinUsable(s.rateDate, s.expenseDate)
    ? { ...base, rateDate: s.rateDate }
    : base;
}

export interface UpdateConversionFields {
  originalAmount?: number;
  originalCurrency?: string | null;
  exchangeRate?: number | null;
  rateDate?: string;
}

export function conversionFieldsForUpdate(
  s: ConversionSnapshot,
  existing: {
    originalCurrency?: string;
    rateSource?: 'provider' | 'manual';
    exchangeRate?: number;
  }
): UpdateConversionFields {
  if (!s.isForeign)
    return existing.originalCurrency ? { originalCurrency: null } : {};
  const created = conversionFieldsForCreate(s);
  if (!created) return {};
  // A queued expense stores a manual rate with no `rateSource`; a stored
  // rate with no source is therefore manual too.
  const hadManual =
    existing.rateSource === 'manual' ||
    (existing.rateSource == null && existing.exchangeRate != null);
  const clearingManual = hadManual && s.rateSource !== 'manual';
  return { ...created, ...(clearingManual ? { exchangeRate: null } : {}) };
}

/**
 * Apportions `total` across the items (largest-remainder method) so the set
 * sums exactly to `total` in the currency's precision:
 *
 * - every amount is rounded to the currency;
 * - a positive drift (`total − Σ`) goes one minor unit at a time to the
 *   items whose pre-rounding fractional remainder is largest, a negative one
 *   is taken from the smallest remainders; equal remainders favour the
 *   earlier item;
 * - no item is taken below one minor unit, and every item is lifted to one,
 *   while `total` covers one unit per item. When it does not (1 JPY across
 *   three items), the highest-ranked items get one unit each and the rest
 *   get zero, which the caller's own validation rejects.
 *
 * Only `amount` is rewritten; percentages/shares pass through. Returns
 * `items` itself when nothing changes. Shared by the offline conversion
 * runner and the Splitwise import, which must settle identically or the two
 * paths produce different splits.
 */
export const settleDrift = <T extends { amount: number }>(
  items: T[],
  total: number,
  currency: string
): T[] => {
  if (items.length === 0) return items;
  const factor = 10 ** getCurrencyDecimals(currency);
  // Scaled values are cleaned to 1e-6 of a minor unit so float noise
  // (1.005 × 100 = 100.49999…) cannot flip a rounding or a remainder tie.
  const toUnits = (amount: number) => Math.round(amount * factor * 1e6) / 1e6;
  const scaled = items.map(i => toUnits(i.amount));
  const remainders = scaled.map(x => Math.round((x - Math.floor(x)) * 1e6));
  const totalUnits = Math.round(toUnits(total));
  const n = items.length;
  const byLargest = items
    .map((_, i) => i)
    .sort((a, b) => remainders[b] - remainders[a] || a - b);

  let units: number[];
  if (totalUnits < n) {
    units = new Array<number>(n).fill(0);
    byLargest.slice(0, Math.max(totalUnits, 0)).forEach(i => {
      units[i] = 1;
    });
  } else {
    units = scaled.map(x => Math.max(1, Math.round(x)));
    let drift = totalUnits - units.reduce((s, u) => s + u, 0);
    if (drift > 0) {
      const each = Math.floor(drift / n);
      byLargest.forEach((i, rank) => {
        units[i] += each + (rank < drift % n ? 1 : 0);
      });
    } else {
      const bySmallest = items
        .map((_, i) => i)
        .sort((a, b) => remainders[a] - remainders[b] || a - b);
      while (drift < 0) {
        const open = bySmallest.filter(i => units[i] > 1);
        // Take whole rounds at once so a huge overshoot stays O(n) passes.
        const step = Math.min(
          Math.floor(-drift / open.length),
          ...open.map(i => units[i] - 1)
        );
        if (step > 0) {
          open.forEach(i => {
            units[i] -= step;
          });
          drift += step * open.length;
        } else {
          open.slice(0, -drift).forEach(i => {
            units[i] -= 1;
          });
          drift += Math.min(-drift, open.length);
        }
      }
    }
  }

  const amounts = units.map(u =>
    roundToCurrencyPrecision(u / factor, currency)
  );
  if (amounts.every((a, i) => a === items[i].amount)) return items;
  return items.map((item, i) => ({ ...item, amount: amounts[i] }));
};
